import { useRef, type MouseEvent } from "react";
import type { Vehicle } from "@/domain/models/vehicle";
import { formatMiles, formatPrice } from "@/domain/models/vehicle";
import { Gauge, Fuel, Cog, Zap } from "lucide-react";
import { useI18n } from "@/i18n";

type Props = {
  vehicle: Vehicle;
  onOpen: (v: Vehicle) => void;
  index?: number;
};

export function VehicleCard({ vehicle, onOpen, index = 0 }: Props) {
  const { t } = useI18n();
  const ref = useRef<HTMLButtonElement>(null);

  const onMove = (e: MouseEvent<HTMLButtonElement>) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    const rx = (0.5 - y) * 8;
    const ry = (x - 0.5) * 10;
    el.style.setProperty("--rx", `${rx}deg`);
    el.style.setProperty("--ry", `${ry}deg`);
    el.style.setProperty("--mx", `${x * 100}%`);
    el.style.setProperty("--my", `${y * 100}%`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty("--rx", `0deg`);
    el.style.setProperty("--ry", `0deg`);
  };

  return (
    <button
      ref={ref}
      onClick={() => onOpen(vehicle)}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      className="group relative block w-full text-left [perspective:1200px] animate-fade-in"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div
        className="relative overflow-hidden rounded-2xl border border-border bg-card transition-[transform,box-shadow] duration-500 group-hover:shadow-[var(--shadow-elevated)]"
        style={{
          transform: "rotateX(var(--rx,0deg)) rotateY(var(--ry,0deg)) translateZ(0)",
          transformStyle: "preserve-3d",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{
            background:
              "radial-gradient(400px circle at var(--mx,50%) var(--my,50%), oklch(0.72 0.17 245 / 0.18), transparent 45%)",
          }}
        />

        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          <img
            src={vehicle.image}
            alt={`${vehicle.year} ${vehicle.brand} ${vehicle.model}`}
            loading={index < 3 ? "eager" : "lazy"}
            width={1200}
            height={800}
            className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.06]"
            style={{ transform: "translateZ(30px)" } as React.CSSProperties}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-card via-card/20 to-transparent" />
          {vehicle.tag && (
            <span className="absolute left-4 top-4 rounded-full bg-primary/95 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-primary-foreground shadow-lg">
              {vehicle.tag}
            </span>
          )}
          {!vehicle.available && (
            <span className="absolute right-4 top-4 rounded-full bg-background/90 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground backdrop-blur">
              {t.vehicle.reserved}
            </span>
          )}
          <div className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/10 to-transparent transition-transform duration-[1200ms] group-hover:translate-x-full" />
        </div>

        <div className="relative p-5" style={{ transform: "translateZ(20px)" } as React.CSSProperties}>
          <div className="flex items-baseline justify-between gap-4">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                {vehicle.brand} · {vehicle.year}
              </div>
              <h3 className="mt-1 truncate font-display text-lg font-semibold">{vehicle.model}</h3>
            </div>
            <div className="shrink-0 text-right">
              <div className="font-display text-xl font-bold text-primary">
                {formatPrice(vehicle.price)}
              </div>
              <div className="text-[11px] text-muted-foreground">{t.vehicle.orLease}</div>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-4 gap-2 border-t border-border pt-4 text-[11px]">
            <Stat icon={<Zap className="h-3.5 w-3.5" />} label={`${vehicle.power} hp`} />
            <Stat icon={<Gauge className="h-3.5 w-3.5" />} label={formatMiles(vehicle.mileage)} />
            <Stat icon={<Cog className="h-3.5 w-3.5" />} label={vehicle.transmission.slice(0, 4)} />
            <Stat icon={<Fuel className="h-3.5 w-3.5" />} label={vehicle.fuel} />
          </div>
        </div>
      </div>
    </button>
  );
}

function Stat({ icon, label }: { icon: React.ReactNode; label: string }) {
  return (
    <div className="flex flex-col items-center gap-1 text-muted-foreground">
      <span className="text-primary">{icon}</span>
      <span className="truncate">{label}</span>
    </div>
  );
}
